import { createTheme } from "@mui/material/styles";
/**
 * Styles for the DataGrid component from MUI
 * @param {boolean} isSmallScreen - Whether the screen is small
 * @returns {CSSProperties} - The styles for the DataGrid
 */
export const getDataGridStyles = (_isSmallScreen: boolean = false) => ({
    fontFamily: "Plus Jakarta Sans",
    // entire table styling border
    borderBottom: 1,
    borderTopRightRadius: 6,
    borderTopLeftRadius: 6,
    borderBottomRightRadius: 6,
    borderBottomLeftRadius: 6,
    borderRight: 1,
    borderLeft: 1,
    borderTop: 1,
    borderColor: "#E4E7EC", // gray-200
    "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
        outline: "none",
    },
    // remove padding at beginning of column header container so that text is inline with column text
    "& .datagrid-table--header .MuiDataGrid-columnHeaderTitleContainer": {
        padding: 0,
        width: 200,
        fontSize: 10,
    },
    // set header font styling
    "& .MuiDataGrid-columnHeaderTitle": {
        color: "#1a7c7f",
        fontWeight: 600,
        fontSize: 12,
    },
    // set header background color
    "& .datagrid-table--header": {
        backgroundColor: "#F9FAFB",
        color: "#1a7c7f",
        fontWeight: 600,
        fontSize: 10,
        paddingLeft: 2,
    },
    "& .datagrid-table-default-document-cell": {
        backgroundColor: "#F9FAFB",
        color: "#1a7c7f",
        fontWeight: 600,
        fontSize: 10,
        paddingLeft: 2,
    },
    "& .MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizable)": {
        display: "none",
    },
    // Remove header focus outline
    "& .MuiDataGrid-columnHeader:focus,.MuiDataGrid-cell:focus": {
        outline: "none",
    },
    // All this to remove the focus outline on the cell when editing - sheesh
    "& .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within": {
        outline: "none",
        outlineOffset: "10px",
    },
    "& .MuiDataGrid-cell--editing": {
        //outlineOffset: "10px",
        outline: "1px solid #D0D5DD",
        border: "1px solid #D0D5DD",
        boxShadow: "none",
        backgroundColor: "#F9FAFB",
        "& .MuiInputBase-root": {
            outlineOffset: "10px",
            outline: "1px solid #26B5BB",
            border: "none",
            boxShadow: "none",
            "& .MuiInputBase-input:focus": {
                outline: "1px solid #26B5BB",
                border: "none",
                boxShadow: "none",
            },
            "&.Mui-focused": {
                outline: "1px solid #26B5BB",
                border: "1px solid #26B5BB",
                boxShadow: "0 0 0 1px #26B5BB",
                outlineOffset: "10px",
            },
        },
    },
    // selected row styling:
    "& .MuiDataGrid-row.Mui-selected": {
        backgroundColor: "#EAFAFB",
    },

    // selected hover row styling
    "& .MuiDataGrid-row.Mui-selected:hover": {
        backgroundColor: "#F9FAFB",
    },
    // editing row background color
    "& .MuiDataGrid-row--editing .MuiDataGrid-cell": {
        backgroundColor: "#F9FAFB",
    },
    // Row styling
    "& .datagrid-table--row": {
        backgroundColor: "#FFF",
    },
    "& .datagrid-table--row-default-document": {
        backgroundColor: "#F9FAFB",
    },
    // hover row styling
    "& .datagrid-table--row:hover": {
        backgroundColor: "#FCFCFD",
        cursor: "pointer",
    },
    "& .datagrid-table--cell": {
        paddingLeft: 2,
    },
    // remove borders and separators
    "& .datagrid-table--column-cell": {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingLeft: 2,
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
        backgroundColor: "#F9FAFB",
        borderRight: "none",
        paddingLeft: 1.5,
    },
    // Selection column styling
    "& .MuiDataGrid-columnHeader--checkbox": {
        backgroundColor: "#F9FAFB",
        borderRight: "none",
    },
    "& .MuiDataGrid-cell--checkbox": {
        borderRight: "none",
        paddingLeft: "8px",
        paddingRight: "8px",
    },
    // Checkbox styling
    "& .MuiDataGrid-checkboxInput": {
        borderRadius: "8px",
        color: "#26B5BB",

        "&.Mui-checked": {
            color: "#26B5BB",
        },
        "&.Mui-indeterminate": {
            color: "#26B5BB",
        },
        "&:hover": {
            backgroundColor: "rgba(26, 124, 127, 0.04)",
        },
        "&.Mui-focusVisible": {
            outline: "2px solid #26B5BB",
            outlineOffset: "2px",
        },
        // Unchecked state - gray border
        "&:not(.Mui-checked):not(.Mui-indeterminate)": {
            color: "#E4E7EC",
        },
    },
    // Footer styling
    "& .MuiDataGrid-footerContainer": {
        backgroundColor: "#F9FAFB",
    },
    "& .MuiDataGrid-footerRowCount": {
        color: "#1a7c7f",
        fontSize: "14px",
        fontWeight: 500,
    },
    "& .MuiDataGrid-footerSelectedRowCount": {
        color: "#1a7c7f",
        fontSize: "14px",
        fontWeight: 600,
    },
    "& .MuiDataGrid-footerPagination": {
        color: "#1a7c7f",
    },
    "& .MuiDataGrid-footerPagination .MuiTablePagination-selectLabel": {
        color: "#1a7c7f",
        fontSize: "14px",
        fontWeight: 500,
    },
    "& .MuiDataGrid-footerPagination .MuiTablePagination-displayedRows": {
        color: "#1a7c7f",
        fontSize: "14px",
        fontWeight: 500,
    },
    "& .MuiDataGrid-footerPagination .MuiTablePagination-actions": {
        "& .MuiIconButton-root": {
            color: "#1a7c7f",
            "&:hover": {
                backgroundColor: "rgba(71, 84, 103, 0.04)",
            },
            "&.Mui-disabled": {
                color: "#D0D5DD",
            },
        },
    },
    // Pagination select styling
    "& .MuiDataGrid-footerPagination .MuiSelect-select": {
        color: "#1a7c7f",
        fontSize: "14px",
        fontWeight: 500,
        backgroundColor: "#FFFFFF",
        border: "1px solid #D0D5DD",
        borderRadius: "6px",
        padding: "4px 8px",
        "&:hover": {
            borderColor: "#26B5BB",
        },
        "&:focus": {
            borderColor: "#26B5BB",
            outline: "2px solid #26B5BB",
            outlineOffset: "2px",
        },
    },
    // Page size options styling
    "& .MuiDataGrid-footerPagination .MuiTablePagination-select": {
        "& .MuiMenuItem-root": {
            color: "#1a7c7f",
            fontSize: "14px",
            fontWeight: 500,
            "&:hover": {
                backgroundColor: "rgba(26, 124, 127, 0.04)",
            },
            "&.Mui-selected": {
                backgroundColor: "rgba(26, 124, 127, 0.08)",
                color: "#1a7c7f",
                fontWeight: 600,
            },
        },
    },
});

export const toolTipStyles = { zIndex: 9999, fontSize: "12px", fontFamily: "Plus Jakarta Sans" };

// MUI theme provider with custom theme
export const MUITheme = createTheme({
    palette: {
        primary: { main: "#26B5BB" }, // max-900 (AA on gray-100)
        secondary: { main: "#219DA2" }, // max-800 example
        error: { main: "#F04437" }, // error.200
        warning: { main: "#FFC123" }, // tropicana.500 example
        info: { main: "#FE2962" }, // blaze.500 example
        success: { main: "#2EDEE5" }, // max.500 example
    },
    components: {
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    "&:focus-visible": {
                        outline: "2px solid #26B5BB",
                        outlineOffset: "2px",
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    "&:focus-visible": {
                        outline: "2px solid #26B5BB",
                        outlineOffset: "2px",
                    },
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    "&:focus-visible": {
                        outline: "2px solid #26B5BB",
                        outlineOffset: "2px",
                    },
                },
            },
        },
    },
});
